import { Fragment, useState } from "react";
import useSWR from "swr";
import { fetchWalletPositions, type WalletRow, type WalletSort } from "../api";
import {
	brierClass,
	fmtCents,
	fmtNum,
	fmtPp,
	fmtRoi,
	fmtUsd,
	shortAddr,
	signClass,
	fmtPct,
	fmtViews,
	profileUrl,
} from "../lib/format";
import { subMeta } from "../lib/meta";

interface Col {
	key: string;
	label: string;
	title: string;
	sort?: WalletSort;
	hideOnMobile?: boolean;
	right?: boolean;
}

const COLS: Col[] = [
	{ key: "wallet", label: "Wallet", title: "Click the address to open the wallet page. Click the row for its positions." },
	{ key: "type", label: "Type", title: "Rule-based label. Hover a badge for the rule." },
	{ key: "views", label: "Views", sort: "views", right: true, hideOnMobile: true, title: "Views of this wallet's Polymarket profile page (refreshed daily). Click the 🔗 next to an address to open it." },
	{ key: "pos", label: "Pos", sort: "resolved", right: true, hideOnMobile: true, title: "Resolved positions / all positions. Sorts by resolved count." },
	{ key: "vol", label: "Volume", sort: "volume", right: true, hideOnMobile: true, title: "Total dollars staked." },
	{ key: "win", label: "Win %", right: true, title: "Share of resolved positions that won. Counts each position once." },
	{ key: "entry", label: "Entry", right: true, hideOnMobile: true, title: "Mean entry price per position (same weighting as Win %). The dollar-weighted entry is in the tooltip." },
	{ key: "roi", label: "ROI", sort: "ev", right: true, title: "Return per $1 staked if held to resolution. Dollar-weighted, so one big loss can outweigh many small wins." },
	{ key: "edge", label: "Edge", sort: "edge", right: true, title: "Mean of (won − entry price) per position. Positive = won more often than the price implied." },
	{ key: "lcb", label: "Edge (95% low)", sort: "edge_lcb", right: true, hideOnMobile: true, title: "Conservative edge: mean minus 1.645 standard errors. Needs 5+ resolved positions. Sorting by this ignores lucky small samples." },
	{ key: "brier", label: "Brier", sort: "brier", right: true, hideOnMobile: true, title: "Mean (entry − outcome)². Lower is better; 0.25 is a coin flip. Low values can just mean they buy heavy favorites." },
	{ key: "clv", label: "CLV", sort: "clv", right: true, hideOnMobile: true, title: "Current price minus entry, dollar-weighted. Works on open markets, no need to wait for resolution." },
	{ key: "z", label: "z", sort: "z", right: true, hideOnMobile: true, title: "Edge divided by its standard error. |z| > 2 is unlikely to be luck for one wallet, but with hundreds of wallets some will clear it by chance." },
];

function Positions({ id }: { id: string }) {
	const { data, error, isLoading } = useSWR(["walletPositions", id], () =>
		fetchWalletPositions(id),
	);
	if (isLoading) return <div className="p-3 text-xs font-mono opacity-60">Loading positions…</div>;
	if (error || !data) return <div className="p-3 text-xs font-mono text-error">Could not load positions.</div>;
	return (
		<div className="overflow-x-auto">
			<table className="table table-xs">
				<thead>
					<tr className="text-[10px] uppercase opacity-70">
						<th>Market</th>
						<th>Side</th>
						<th className="text-right">Entry</th>
						<th className="text-right">$</th>
						<th className="text-right">Now</th>
						<th>Result</th>
						<th className="text-right">P&amp;L</th>
					</tr>
				</thead>
				<tbody>
					{data.data.map((p) => (
						<tr key={p.token_id}>
							<td className="max-w-[260px] truncate" title={p.question ?? ""}>
								{p.question ?? shortAddr(p.condition_id)}
								{p.hedged ? <span className="badge badge-ghost badge-xs ml-1" title="This wallet also holds the opposite outcome">hedged</span> : null}
							</td>
							<td>{p.outcome ?? dashStr}</td>
							<td className="text-right font-mono">{fmtCents(p.entry)}</td>
							<td className="text-right font-mono">{fmtUsd(p.volume_usd)}</td>
							<td className="text-right font-mono">{fmtCents(p.last_price)}</td>
							<td>
								{!p.resolved ? (
									<span className="badge badge-ghost badge-xs">open</span>
								) : p.won ? (
									<span className="badge badge-success badge-xs">won</span>
								) : (
									<span className="badge badge-error badge-xs">lost</span>
								)}
							</td>
							<td className={`text-right font-mono ${signClass(p.pnl_usd)}`}>
								{p.pnl_usd === null ? dashStr : fmtUsd(p.pnl_usd)}
							</td>
						</tr>
					))}
				</tbody>
			</table>
			{data.data.length >= 200 ? (
				<div className="p-2 text-[10px] opacity-60">Showing the 200 largest positions.</div>
			) : null}
		</div>
	);
}

const dashStr = "–";

export function WalletTable({
	rows,
	sort,
	dir,
	onSort,
}: {
	rows: WalletRow[];
	sort: WalletSort;
	dir: "asc" | "desc";
	onSort: (s: WalletSort) => void;
}) {
	const [open, setOpen] = useState<string | null>(null);
	return (
		<div className="overflow-x-auto">
			<table className="table table-xs md:table-sm">
				<thead>
					<tr className="text-[10px] uppercase tracking-wider">
						{COLS.map((c) => (
							<th
								key={c.key}
								title={c.title}
								className={`${c.right ? "text-right" : ""} ${c.hideOnMobile ? "hidden md:table-cell" : ""} ${c.sort ? "cursor-pointer select-none hover:text-primary" : ""}`}
								onClick={c.sort ? () => onSort(c.sort as WalletSort) : undefined}
							>
								{c.label}
								{c.sort && c.sort === sort ? (dir === "asc" ? " ▲" : " ▼") : ""}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((r) => {
						const m = subMeta(r.subcategory);
						const id = r.wallet ?? r.account;
						const isOpen = open === r.account;
						return (
							<Fragment key={r.account}>
								<tr className="hover cursor-pointer" onClick={() => setOpen(isOpen ? null : r.account)}>
									<td className="font-mono text-xs whitespace-nowrap">
										{r.wallet ? (
											<a className="link link-hover" href={`/dashboard/users/${r.wallet}`} onClick={(e) => e.stopPropagation()}>
												{shortAddr(r.wallet)}
											</a>
										) : (
											<span className="opacity-60" title="Address not stored for this wallet">#{r.account.slice(0, 8)}</span>
										)}
										{r.wallet ? (
											<a className="ml-1 opacity-60 hover:opacity-100" href={profileUrl(r.wallet)} target="_blank" rel="noreferrer" title="Open Polymarket profile" onClick={(e) => e.stopPropagation()}>🔗</a>
										) : null}
										<div className="md:hidden text-[10px] opacity-60" title="Polymarket profile views">👁 {fmtViews(r.views)}</div>
									</td>
									<td className="whitespace-nowrap">
										<span className={`badge badge-sm whitespace-nowrap ${m.badge}`} title={`${m.blurb} (${r.reason})`}>{m.label}</span>
										{r.also_sniper && r.subcategory !== "sniper" ? (
											<span className="badge badge-ghost badge-xs ml-1 whitespace-nowrap" title={`${r.sniper_fills} fills at price ≥ 95¢`}>+sniper</span>
										) : null}
									</td>
									<td className="text-right font-mono text-xs hidden md:table-cell" title="Polymarket profile views">{fmtViews(r.views)}</td>
									<td className="text-right font-mono text-xs hidden md:table-cell">{r.n_resolved}/{r.n_positions}</td>
									<td className="text-right font-mono text-xs hidden md:table-cell">{fmtUsd(r.volume_usd)}</td>
									<td className="text-right font-mono text-xs">{fmtPct(r.win_rate)}</td>
									<td className="text-right font-mono text-xs hidden md:table-cell" title={`dollar-weighted: ${fmtCents(r.entry_vwap)}`}>{fmtCents(r.entry_mean)}</td>
									<td className={`text-right font-mono text-xs ${signClass(r.ev_per_usd)}`}>{fmtRoi(r.ev_per_usd)}</td>
									<td className={`text-right font-mono text-xs ${signClass(r.edge)}`}>{fmtPp(r.edge)}</td>
									<td className={`text-right font-mono text-xs hidden md:table-cell ${signClass(r.edge_lcb)}`}>{fmtPp(r.edge_lcb)}</td>
									<td className={`text-right font-mono text-xs hidden md:table-cell ${brierClass(r.brier)}`}>{fmtNum(r.brier, 3)}</td>
									<td className={`text-right font-mono text-xs hidden md:table-cell ${signClass(r.clv)}`}>{fmtPp(r.clv)}</td>
									<td className={`text-right font-mono text-xs hidden md:table-cell ${signClass(r.z_score)}`}>{fmtNum(r.z_score, 1)}</td>
								</tr>
								{isOpen ? (
									<tr className="bg-base-300/40">
										<td colSpan={COLS.length} className="p-0">
											<Positions id={id} />
										</td>
									</tr>
								) : null}
							</Fragment>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}
